/**
 * @description ForgotPassword component for password reset request
 *
 * Key features:
 * - Email input form
 * - Calls POST /api/auth/forgot-password
 * - Shows "check your email" message on success
 * - Prevents email enumeration (always shows success)
 * - Dark auth-card styling aligned with SignIn / SignUp (brand tokens)
 *
 * @dependencies
 * - AuthLayout: Shared auth page layout
 * - apiClient: API calls to backend
 *
 * @notes
 * - Backend always returns success to prevent email enumeration attacks
 * - User receives email with reset link if account exists
 * - Do not use gray-900 / gray-700 text on this card — AuthLayout is dark
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiClient from '../../services/api/apiClient';
import AuthLayout from './AuthLayout';
import { useBranding } from '../../contexts/BrandingContext';
import { resolveAuthSubdomain, withAuthSubdomain } from '../../utils/authTenantQuery';

function ForgotPassword() {
  const { slug: brandingSlug } = useBranding();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isEmailSent, setIsEmailSent] = useState(false);

  const validateForm = () => {
    const errors = {};

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (value) => {
    setEmail(value);
    if (fieldErrors.email) {
      setFieldErrors({});
    }
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const subdomain = await resolveAuthSubdomain({ brandingSlug });
      const forgotUrl = withAuthSubdomain('/auth/forgot-password', subdomain);

      await apiClient.post(forgotUrl, {
        email: email.trim().toLowerCase()
      });

      setIsEmailSent(true);
    } catch (err) {
      console.error('Forgot password error:', err);

      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setIsEmailSent(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setIsEmailSent(false);
    setEmail('');
    setError(null);
    setFieldErrors({});
  };

  if (isEmailSent) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center py-8">
          <div className="w-16 h-16 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Check your email
          </h1>
          <p className="text-gray-400 mb-2 text-sm">
            If an account exists for <span className="font-medium text-white">{email}</span>, we&apos;ve sent password reset instructions.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Check your spam folder if you don&apos;t see it within a few minutes.
          </p>
          <Link
            to="/sign-in"
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] text-center shadow-2xl"
          >
            Back to sign in
          </Link>
          <button
            type="button"
            onClick={handleTryAgain}
            className="mt-4 text-sm font-bold text-brand hover:underline underline-offset-4 transition-all"
          >
            Didn&apos;t receive an email? Try again
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      helper={
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-gray-500">Remember your password?</span>
          <Link
            to="/sign-in"
            className="font-bold text-brand hover:underline underline-offset-4 transition-all"
          >
            Sign in
          </Link>
        </div>
      }
    >
      <div className="space-y-8">
        <div>
          <Link
            to="/sign-in"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-brand hover:text-brand-hover transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
          <div className="text-center mt-6">
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Forgot password</h1>
            <p className="text-gray-500 text-sm font-medium">We&apos;ll email you a reset link</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300 transition-colors"
                aria-label="Dismiss error"
              >
                <span className="text-lg leading-none">&times;</span>
              </button>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="email" className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => handleInputChange(e.target.value)}
              className={`w-full bg-white/[0.03] border rounded-2xl px-4 py-3.5 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none ${
                fieldErrors.email
                  ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                  : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
              }`}
              placeholder="name@company.com"
              disabled={isSubmitting}
              autoFocus
            />
            {fieldErrors.email && (
              <p className="text-xs text-red-400 ml-1">{fieldErrors.email}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 shadow-2xl"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              'Send reset link'
            )}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}

export default ForgotPassword;
